import { ENABLE_PERF_LOGS, PERF_LOG_INTERVAL_MS, PERF_LOG_MAX_SAMPLES, PERF_LOG_BUFFER_LIMIT, PERF_LOG_ENDPOINT } from './constants.js';

export class PerformanceLogger {
    constructor() {
        this.enabled = ENABLE_PERF_LOGS && typeof performance !== 'undefined';
        this.sections = new Map();
        this.counters = new Map();
        this.frames = 0;
        this.lastLog = this.enabled ? performance.now() : 0;
        this.logBuffer = [];
    }

    wrap(sectionName, fn) {
        if (!this.enabled) {
            return fn();
        }

        const start = performance.now();
        try {
            return fn();
        } finally {
            const duration = performance.now() - start;
            const bucket = this.sections.get(sectionName) || { total: 0, max: 0, samples: 0 };
            bucket.total += duration;
            bucket.samples += 1;
            if (duration > bucket.max) {
                bucket.max = duration;
            }
            if (bucket.samples > PERF_LOG_MAX_SAMPLES) {
                bucket.total *= 0.5;
                bucket.samples = Math.ceil(bucket.samples * 0.5);
                bucket.max *= 0.8;
            }
            this.sections.set(sectionName, bucket);
        }
    }

    accumulate(counterName, value) {
        if (!this.enabled) return;
        const bucket = this.counters.get(counterName) || { total: 0, samples: 0, last: value };
        bucket.total += value;
        bucket.samples += 1;
        bucket.last = value;
        this.counters.set(counterName, bucket);
    }

    frameEnd() {
        if (!this.enabled) return;
        this.frames += 1;
        const now = performance.now();
        if (now - this.lastLog < PERF_LOG_INTERVAL_MS) {
            return;
        }

        const sectionSummary = [];
        this.sections.forEach((bucket, name) => {
            const avg = bucket.total / bucket.samples;
            sectionSummary.push({ name, avg, max: bucket.max, samples: bucket.samples });
        });
        sectionSummary.sort((a, b) => b.avg - a.avg);

        const counterSummary = [];
        this.counters.forEach((bucket, name) => {
            counterSummary.push({ name, avg: bucket.total / bucket.samples, last: bucket.last, samples: bucket.samples });
        });
        counterSummary.sort((a, b) => b.avg - a.avg);

        // 计算FPS和关键指标
        const elapsedSeconds = (now - this.lastLog) / 1000;
        const fps = this.frames / elapsedSeconds;

        const payload = {
            timestamp: Date.now(),
            windowFrames: this.frames,
            fps: fps,
            sections: sectionSummary,
            topSection: sectionSummary[0] || null,
            counters: counterSummary,
            topCounter: counterSummary[0] || null,
            // 提取关键性能指标用于快速查看
            physicsTime: sectionSummary.find(s => s.name === 'physics')?.avg || 0,
            renderTime: sectionSummary.find(s => s.name === 'render')?.avg || 0,
            physicsBodies: counterSummary.find(c => c.name === 'physicsBodies')?.last || 0,
            physicsActiveBodies: counterSummary.find(c => c.name === 'physicsActiveBodies')?.last || 0,
            physicsContacts: counterSummary.find(c => c.name === 'physicsContacts')?.last || 0,
            enemyCount: counterSummary.find(c => c.name === 'enemyCount')?.last || 0,
            gameObjects: counterSummary.find(c => c.name === 'gameObjects')?.last || 0,
            renderTriangles: counterSummary.find(c => c.name === 'renderTriangles')?.last || 0,
            renderCalls: counterSummary.find(c => c.name === 'renderCalls')?.last || 0,
            pendingEnemySpawns: counterSummary.find(c => c.name === 'pendingEnemySpawns')?.last || 0
        };
        const serialized = JSON.stringify(payload);

        // 保存到 localStorage 用于性能诊断工具
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.setItem('gamePerformanceLog', serialized);
            } catch (e) {
                // localStorage 可能已满，忽略错误
            }
        }

        if (this.logBuffer.length >= PERF_LOG_BUFFER_LIMIT) {
            this.logBuffer.shift();
        }
        this.logBuffer.push(serialized);

        this.sections.clear();
        this.counters.clear();
        this.frames = 0;
        this.lastLog = now;
    }

    saveLogs() {}
}
